import { createContext, useContext, useEffect, useState } from "react";

import { AuthState } from "@/types/auth.types";
import { useAuthUser } from "@/features/auth/queries/auth.get.queries";
import { useLogout } from "@/features/auth/queries/auth.update.queries";

interface AuthContextType {
  // TODO: add types
  user: any | null | undefined;
  authState: AuthState;
  setAuthState: (state: AuthState) => void;
  logout: () => void;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  authState: AuthState.IDLE,
  setAuthState: () => console.warn("AuthContext not initialized"),
  logout: () => console.warn("AuthContext not initialized"),
  refetch: () => console.warn("AuthContext not initialized"),
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(AuthState.IDLE);
  const { data: user , isLoading, refetch } = useAuthUser();

  const logoutMutation = useLogout();

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setAuthState(AuthState.UNAUTHENTICATED);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    // With HTTP-only cookies, we determine auth state based on the API response
    // If user data is null or there's an error, user is not authenticated
    const newAuthState = 
      user ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED;

    setAuthState(newAuthState);
  }, [user, isLoading]);

  const contextValue = {
    user: user?.data ?? undefined,
    authState: isLoading ? AuthState.AUTHENTICATING : authState,
    setAuthState,
    logout: handleLogout,
    refetch,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used inside context");
  }

  return context;
}
